import classNames from 'classnames'
import { type PropsWithChildren, useRef, useState } from 'react'
import { Typography } from 'shared/ui/Typography'
import { Icon, type IIconsVector } from 'shared/ui/Icon'
import { Tooltip, type ITooltipProps } from 'shared/ui/Tooltip'

import styles from './styles.module.scss'

export type ISectionCollapseProps = PropsWithChildren<{
  icon: IIconsVector
  title: string
  tooltipProps?: ITooltipProps
  error?: string | null
  onChange?: (expanded: boolean) => void
  isShowBottomBorder?: boolean
  defaultExpanded?: boolean
}>

export const SectionCollapse = ({
  icon,
  title,
  tooltipProps,
  error,
  children,
  onChange,
  isShowBottomBorder = true,
  defaultExpanded = false,
}: ISectionCollapseProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLElement | null>(null)
  const [expanded, expand] = useState(defaultExpanded)

  const handleClick = () => {
    const isExpanded = !expanded

    expand(isExpanded)
    onChange?.(isExpanded)
  }

  return (
    <section
      className={classNames(styles.container, {
        [styles.expanded]: expanded,
      })}
    >
      <header
        ref={ref}
        className={styles.header}
        onClick={handleClick}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <span className={styles.title}>
          <span className={styles.icon}>
            <Icon icon={icon} fontSize={16} />
          </span>
          <Typography className={styles.text} variant='body-lg-medium' ariaLabel={'title'}>
            {title}
            {!!tooltipProps && (
              <Tooltip {...tooltipProps}>
                <span className={styles.tooltipIcon}>
                  <Icon icon='question' fontSize={16} />
                </span>
              </Tooltip>
            )}
          </Typography>
          {!!error && (
            <Tooltip
              color='red'
              placement='bottom'
              open={open}
              label={error}
              breakWord
              maxWidth={360}
            >
              <Icon icon='alertTriangle' fontSize={16} color={'var(--content-negative-primary)'} />
            </Tooltip>
          )}
        </span>

        <Icon
          fontSize={16}
          color={'var(--content-primary-tertiary)'}
          icon={expanded ? 'chevronUp' : 'chevronDown'}
        />
      </header>
      {expanded && (
        <div className={classNames(styles.content, { [styles.bottomBorder]: isShowBottomBorder })}>
          {children}
        </div>
      )}
    </section>
  )
}
